import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Copy, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import type {
  CreateInstanceRequest,
  CreateInstanceResponse,
  InstancePlan,
} from "@/types/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NAME_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateInstanceDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation("instances");
  const qc = useQueryClient();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [plan, setPlan] = React.useState<InstancePlan>("self-hosted");
  const [result, setResult] = React.useState<CreateInstanceResponse | null>(null);

  React.useEffect(() => {
    if (!open) {
      setName("");
      setEmail("");
      setPlan("self-hosted");
      setResult(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateInstanceRequest) =>
      api.post<CreateInstanceResponse>("/api/admin/create-instance", payload),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ["instances"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("createDialog.createFailed"));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim().toLowerCase();
    const trimmedEmail = email.trim();
    if (!NAME_PATTERN.test(trimmedName)) {
      toast.error(t("createDialog.invalidName"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error(t("createDialog.invalidEmail"));
      return;
    }
    createMutation.mutate({ name: trimmedName, email: trimmedEmail, plan });
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-dialog-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
        {result ? (
          <CreatedResult result={result} onClose={() => onOpenChange(false)} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <h2 id="create-dialog-title" className="text-lg font-semibold">
                {t("createDialog.title")}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("createDialog.description")}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ci-name">{t("createDialog.nameLabel")}</Label>
              <Input
                id="ci-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase())}
                placeholder="acme-corp"
                maxLength={32}
              />
              <p className="text-xs text-muted-foreground">
                {t("createDialog.nameHint")}{" "}
                (<code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">{name || "name"}.neurobots.tech</code>).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ci-email">{t("createDialog.emailLabel")}</Label>
              <Input
                id="ci-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@customer.com"
              />
              <p className="text-xs text-muted-foreground">
                {t("createDialog.emailHintBefore")}{" "}<code className="font-mono text-[11px]">user+{name || "name"}@domain</code>{" "}{t("createDialog.emailHintAfter")}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ci-plan">{t("createDialog.planLabel")}</Label>
              <Select value={plan} onValueChange={(v) => setPlan(v as InstancePlan)}>
                <SelectTrigger id="ci-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self-hosted">{t("createDialog.planSelfHosted")}</SelectItem>
                  <SelectItem value="managed">{t("createDialog.planManaged")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={createMutation.isPending}
              >
                {t("createDialog.cancel")}
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("createDialog.submit")}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function CreatedResult({
  result,
  onClose,
}: {
  result: CreateInstanceResponse;
  onClose: () => void;
}) {
  const { t } = useTranslation("instances");
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("createDialog.result.title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{result.message}</p>
      </div>

      <dl className="space-y-2 text-sm">
        <Row label={t("createDialog.result.name")} value={result.instance.name} />
        <Row
          label={t("createDialog.result.url")}
          value={result.instance.url}
          link={result.instance.url}
        />
        <Row label={t("createDialog.result.status")} value={result.instance.status} />
        <Row label={t("createDialog.result.adminEmail")} value={result.user.email} />
      </dl>

      <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm dark:bg-amber-950/40">
        <p className="font-medium text-amber-900 dark:text-amber-200">
          {t("createDialog.result.passwordTitle")}
        </p>
        <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
          {t("createDialog.result.passwordHint", { email: result.user.email })}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 break-all rounded bg-background px-2 py-1.5 font-mono text-xs">
            {result.user.password}
          </code>
          <CopyButton value={result.user.password} />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onClose}>{t("createDialog.result.done")}</Button>
      </div>
    </div>
  );
}

function Row({ label, value, link }: { label: string; value: string; link?: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 text-muted-foreground">{label}</dt>
      <dd className="flex-1 break-all font-mono text-xs">
        {link ? (
          <a
            className="text-primary hover:underline"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </a>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation("instances");
  const [copied, setCopied] = React.useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("createDialog.clipboardUnavailable"));
    }
  }
  return (
    <Button type="button" variant="secondary" size="sm" onClick={copy}>
      {copied ? (
        <>
          <Check className="mr-1 h-3.5 w-3.5" /> {t("createDialog.copied")}
        </>
      ) : (
        <>
          <Copy className="mr-1 h-3.5 w-3.5" /> {t("createDialog.copy")}
        </>
      )}
    </Button>
  );
}
